import { useRef, useState } from 'react'
import { createRoot, Root } from 'react-dom/client'
import { useTranslation } from 'react-i18next'
import type { PublicJobcardView, PublicInspection } from '@garage/shared'
import { Button, DownloadIcon } from '@/ui'
import { PublicInspectionSheet } from './PublicInspectionSheet.js'

interface PublicInspectionPdfButtonProps {
  garage: PublicJobcardView['garage']
  jobcardNumber: string
  car: PublicJobcardView['car']
  inspection: PublicInspection
  token: string
}

/**
 * Button to download the inspection report as a PDF.
 * Renders the report in a hidden container and uses html2pdf to generate the PDF.
 */
export function PublicInspectionPdfButton({
  garage,
  jobcardNumber,
  car,
  inspection,
  token,
}: PublicInspectionPdfButtonProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const rootRef = useRef<Root | null>(null)

  const handleDownload = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Create hidden container for rendering the sheet
      const container = document.createElement('div')
      container.style.position = 'fixed'
      container.style.left = '-10000px'
      container.style.top = '0'
      container.style.width = '794px'
      container.style.visibility = 'hidden'
      document.body.appendChild(container)
      containerRef.current = container

      // Render the sheet into the container
      const root = createRoot(container)
      rootRef.current = root
      root.render(
        <PublicInspectionSheet
          garage={garage}
          jobcardNumber={jobcardNumber}
          car={car}
          inspection={inspection}
          token={token}
        />
      )

      // Wait a bit for React to render
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Wait for all images to load (with timeout)
      const imageElements = container.querySelectorAll('img')
      const imagePromises = Array.from(imageElements).map((img) => {
        return new Promise<void>((resolve) => {
          if (img.complete) {
            resolve()
          } else {
            const onLoad = () => {
              img.removeEventListener('load', onLoad)
              img.removeEventListener('error', onLoad)
              resolve()
            }
            img.addEventListener('load', onLoad)
            img.addEventListener('error', onLoad)
            // Timeout after 5 seconds per image
            setTimeout(resolve, 5000)
          }
        })
      })

      // Overall timeout of 20 seconds
      await Promise.race([
        Promise.all(imagePromises),
        new Promise((resolve) => setTimeout(resolve, 20000)),
      ])

      // Get the page element
      const pageElement = container.querySelector('.page')
      if (!pageElement) {
        throw new Error('Could not find page element')
      }

      // Import html2pdf dynamically
      const html2pdf = (await import('html2pdf.js')).default

      // Generate PDF
      await html2pdf()
        .set({
          filename: `inspection-${jobcardNumber}.pdf`,
          margin: 0,
          image: { type: 'jpeg', quality: 0.92 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: 'css' as const, avoid: 'tr' as const },
        } as any)
        .from(pageElement as HTMLElement)
        .save()
    } catch (err) {
      console.error('PDF generation error:', err)
      setError(t('publicPortal.inspection.pdfFailed'))
    } finally {
      // Clean up
      if (rootRef.current) {
        rootRef.current.unmount()
        rootRef.current = null
      }
      if (containerRef.current && containerRef.current.parentNode) {
        containerRef.current.parentNode.removeChild(containerRef.current)
        containerRef.current = null
      }
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-2">
      <Button
        variant="outlined"
        size="sm"
        icon={<DownloadIcon />}
        onClick={handleDownload}
        disabled={isLoading}
        loading={isLoading}
      >
        {isLoading ? t('publicPortal.inspection.preparingPdf') : t('publicPortal.inspection.downloadPdf')}
      </Button>
      {error && <p className="text-body-sm text-error">{error}</p>}
    </div>
  )
}
